/**
 * useRelayHealth — polls /relay/health (or /health) endpoint for relay server status
 *
 * Returns connection info, uptime, smart relay status.
 * Polls every 30s by default.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export interface RelayHealthData {
  status: 'ok' | 'error';
  uptime: number;
  websocketConnections: number;
  smartRelay: {
    enabled: boolean;
    kalshiConnected?: boolean;
    polymarketConnected?: boolean;
    marketCount?: number;
    orderbookCount?: number;
  };
  lastCheckedMs: number;
}

// Derive health URL from relay HTTP URL.
// In production VITE_RELAY_HTTP_URL is a relative path like "/relay/http" — can't use as
// URL base (would crash). The relay's /health endpoint isn't behind the /relay/ proxy,
// but /relay/test is and returns equivalent health data.
function buildRelayHealthUrl(): string {
  const relayUrl = (import.meta.env.VITE_RELAY_HTTP_URL as string | undefined)?.trim();
  if (relayUrl) {
    // Full URL (dev with explicit override) — derive sibling /health path
    if (/^https?:\/\//i.test(relayUrl)) {
      try {
        return new URL('../health', relayUrl).toString();
      } catch {
        // fall through
      }
    }
    // Relative path (production) — use /relay/test which is proxied and returns health data
    return '/relay/test';
  }
  return import.meta.env.DEV ? 'http://localhost:8787/health' : '/relay/test';
}
const RELAY_HEALTH_URL = buildRelayHealthUrl();

const DEFAULT_POLL_MS = 30_000;

export function useRelayHealth(pollMs = DEFAULT_POLL_MS) {
  const [health, setHealth] = useState<RelayHealthData | null>(null);
  const [relayReachable, setRelayReachable] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const fetchHealth = useCallback(async () => {
    try {
      const resp = await fetch(RELAY_HEALTH_URL, { signal: AbortSignal.timeout(5000) });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setHealth({
        status: 'ok',
        uptime: data.uptime ?? 0,
        websocketConnections: data.connections?.websocket ?? 0,
        smartRelay: {
          enabled: data.smartRelay?.enabled ?? false,
          kalshiConnected: data.smartRelay?.kalshiConnected,
          polymarketConnected: data.smartRelay?.polymarketConnected,
          marketCount: data.smartRelay?.marketCount,
          orderbookCount: data.smartRelay?.orderbookCount,
        },
        lastCheckedMs: Date.now(),
      });
      setRelayReachable(true);
    } catch {
      setHealth((prev) =>
        prev
          ? { ...prev, status: 'error', lastCheckedMs: Date.now() }
          : {
              status: 'error',
              uptime: 0,
              websocketConnections: 0,
              smartRelay: { enabled: false },
              lastCheckedMs: Date.now(),
            }
      );
      setRelayReachable(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    timerRef.current = setInterval(fetchHealth, pollMs);
    return () => clearInterval(timerRef.current);
  }, [fetchHealth, pollMs]);

  return { health, relayReachable };
}
